/* eslint-disable no-undef */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from "react";
import "./css/StaffPage.css";
import { getBdePerformance } from "../api/careerApi";
import {
  FiUsers,
  FiUserCheck,
  FiSearch,
  FiX,
  FiCopy,
  FiTrendingUp,
} from "react-icons/fi";
import { BsCurrencyRupee } from "react-icons/bs";
import AccessControl from "../components/AccessControl";

const ADMIN_PANEL_ROLES = ["SUPERADMIN", "ADMIN", "HR"];

export default function BdePerformancePage() {
  const [bdeList, setBdeList] = useState([]);
  const [summary, setSummary] = useState({ totalDrivers: 0, totalIncentivePaid: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState({ show: false, message: "" });

  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: "" }), 2500);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBdePerformance();
      if (res.success) {
        setBdeList(res.bdeList || []);
        setSummary(res.summary || { totalDrivers: 0, totalIncentivePaid: 0 });
      } else {
        setBdeList([]);
      }
    } catch {
      setBdeList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = bdeList.filter((b) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      b.name?.toLowerCase().includes(q) ||
      b.email?.toLowerCase().includes(q) ||
      b.inviteCode?.toLowerCase().includes(q)
    );
  });

  const copyInviteLink = async (inviteCode) => {
    const base =
      process.env.REACT_APP_DRIVER_INVITE_BASE_URL ||
      "https://app.moovuruli.in/register";
    const link = `${base}?ref=${inviteCode}`;
    try {
      await navigator.clipboard.writeText(link);
      showToast("Invite link copied!");
    } catch {
      showToast(link);
    }
  };

  return (
    <div>
      <AccessControl allowedRoles={ADMIN_PANEL_ROLES} redirectTo="/" />

      {toast.show && (
        <div className="sf-toast sf-toast-success">
          <span><FiCopy size={16} /></span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="sf-page-header section">
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: "#1a1a1a", margin: 0 }}>
            BDE Performance
          </h2>
          <p style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
            Driver onboarding & incentive tracking per BDE
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="stat-grid section">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#EEEDFE" }}>
            <FiUsers size={18} color="#3C3489" />
          </div>
          <div className="stat-label">Active BDEs</div>
          <div className="stat-value">{bdeList.length}</div>
          <div className="stat-change neutral">Field executives</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#E6F1FB" }}>
            <FiUserCheck size={18} color="#185FA5" />
          </div>
          <div className="stat-label">Drivers Onboarded</div>
          <div className="stat-value" style={{ color: "#185FA5" }}>{summary.totalDrivers}</div>
          <div className="stat-change up">Across all BDEs</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#E1F5EE" }}>
            <BsCurrencyRupee size={16} color="#0F6E56" />
          </div>
          <div className="stat-label">Total Incentive Paid</div>
          <div className="stat-value" style={{ color: "#0F6E56" }}>₹{summary.totalIncentivePaid}</div>
          <div className="stat-change up">₹100 per first-ride driver</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="card section">
        <div className="sf-toolbar">
          <div className="search-box">
            <FiSearch size={14} className="search-icon" />
            <input
              placeholder="Search BDE name, email or invite code…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="search-clear" onClick={() => setSearchTerm("")}>
                <FiX size={12} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card section">
        {loading ? (
          <div className="sf-loading" style={{ height: "40vh" }}>
            <div
              className="spinner"
              style={{ width: 24, height: 24, borderColor: "#1d9e75", borderTopColor: "transparent" }}
            ></div>
            <p style={{ marginTop: 12, color: "#888", fontSize: 13 }}>Loading performance…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="sf-empty">
            <FiTrendingUp size={40} color="#ddd" />
            <p>No BDE staff found</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="sf-table">
              <thead>
                <tr>
                  <th>BDE</th>
                  <th>Invite Code</th>
                  <th>Total Onboarded</th>
                  <th>Approved Drivers</th>
                  <th>First-Ride Completed</th>
                  <th>Incentive Earned</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <tr key={b.staffId}>
                    <td>
                      <div className="sf-member-cell">
                        <div className="sf-avatar">{b.name?.charAt(0)?.toUpperCase()}</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{b.name}</div>
                          <div style={{ fontSize: 11, color: "#888" }}>{b.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <button
                        onClick={() => copyInviteLink(b.inviteCode)}
                        title="Copy invite link"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 12,
                          fontFamily: "monospace",
                          background: "#f3f4f6",
                          border: "none",
                          borderRadius: 6,
                          padding: "4px 8px",
                          cursor: "pointer",
                        }}
                      >
                        {b.inviteCode} <FiCopy size={11} />
                      </button>
                    </td>
                    <td style={{ fontSize: 13, fontWeight: 600 }}>{b.totalOnboarded}</td>
                    <td style={{ fontSize: 13, color: "#555" }}>{b.approvedOnboarded}</td>
                    <td style={{ fontSize: 13, color: "#0F6E56", fontWeight: 600 }}>
                      {b.incentivizedDrivers}
                    </td>
                    <td style={{ fontSize: 13, fontWeight: 700, color: "#185FA5" }}>
                      ₹{b.totalIncentiveEarned}
                    </td>
                    <td>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: "4px 10px",
                          borderRadius: 20,
                          color: b.isActive ? "#0F6E56" : "#A32D2D",
                          background: b.isActive ? "#E1F5EE" : "#FCEBEB",
                        }}
                      >
                        {b.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}